// ./src/components/features/Vehicle/VehicleCardQuickActions/VehicleCardQuickActions.tsx
//
// Quick action buttons for vehicle cards (dashboard mode only).
//
// Shows shortcut buttons for common actions: refuel, expense, checkpoint,
// travel, parking. Rendered at the bottom of VehicleCard on the dashboard.
//
// Edit/Delete actions are NO LONGER handled here — they have moved to
// VehicleCard's hover overlay (desktop) and swipe actions (mobile),
// consistent with RecordCard's pattern.

import React from "react";
import {
  Group,
  Stack,
  Tooltip,
  UnstyledButton,
  useMantineTheme,
} from "@mantine/core";
import { useTranslation } from "react-i18next";

import { ReceiptScanResult } from "@/utils/api/ScannerService";

import { DetectLocationFn } from "@/hooks/useScanner";

import { AppIcon } from "@/components/generic/AppIcon";

import { ScanReceiptButton } from "@/components/features/ScanReceiptButton";

// ============================================
// Types
// ============================================

export type QuickActionType =
  | "refuel"
  | "expense"
  | "checkpoint"
  | "travel"
  | "parking";

export interface QuickAction {
  type: QuickActionType;
  labelKey: string;
  iconName: string;
  color: string;
}

export interface VehicleCardQuickActionsProps {
  /** Car ID for action handlers */
  carId: string;

  /** Which quick actions to show (defaults to all) */
  actions?: QuickActionType[];

  /** Size variant - "mobile" provides larger touch targets and brighter colors */
  size?: "sm" | "md" | "lg" | "mobile";

  /** Handler for quick action click */
  onAction?: (action: QuickActionType, carId: string) => void;

  /** Whether to show the scan receipt button */
  showScanReceipt?: boolean;

  /** Handler for scan receipt completion */
  onScanComplete?: (result: ReceiptScanResult, carId?: string | null) => void;

  /** Optional location detection function for scan pipeline */
  onDetectLocation?: DetectLocationFn;

  /** Custom class name */
  className?: string;
}

// ============================================
// Constants
// ============================================

const QUICK_ACTIONS: Record<QuickActionType, Omit<QuickAction, "type">> = {
  refuel: {
    labelKey: "common:actions.addRefuel",
    iconName: "GasStation",
    color: "cyan",
  },
  expense: {
    labelKey: "common:actions.addExpense",
    iconName: "Receipt",
    color: "yellow",
  },
  checkpoint: {
    labelKey: "common:actions.addCheckpoint",
    iconName: "MapPin",
    color: "brand",
  },
  travel: {
    labelKey: "common:actions.addTravel",
    iconName: "Route",
    color: "grape",
  },
  parking: {
    labelKey: "common:actions.parking",
    iconName: "ParkingCircle",
    color: "blue",
  },
};

const DEFAULT_ACTIONS: QuickActionType[] = [
  "refuel",
  "expense",
  "checkpoint",
  "travel",
  "parking",
];

const SIZE_CONFIG = {
  sm: {
    iconSize: 20,
    iconStroke: 1.5,
    buttonHeight: 36,
    bgShade: 0 as number,
    iconShade: 6 as number,
    hoverShade: 1 as number,
    activeShade: 2 as number,
  },
  md: {
    iconSize: 22,
    iconStroke: 1.5,
    buttonHeight: 42,
    bgShade: 0 as number,
    iconShade: 6 as number,
    hoverShade: 1 as number,
    activeShade: 2 as number,
  },
  lg: {
    iconSize: 24,
    iconStroke: 1.5,
    buttonHeight: 48,
    bgShade: 0 as number,
    iconShade: 6 as number,
    hoverShade: 1 as number,
    activeShade: 2 as number,
  },
  mobile: {
    iconSize: 28,
    iconStroke: 1.8,
    buttonHeight: 48,
    bgShade: 1 as number,
    iconShade: 7 as number,
    hoverShade: 2 as number,
    activeShade: 3 as number,
  },
};

// ============================================
// Action Button Component (shared by all sizes)
// ============================================

interface ActionButtonProps {
  iconName: string;
  label: string;
  color: string;
  iconSize: number;
  iconStroke: number;
  buttonHeight: number;
  bgShade: number;
  iconShade: number;
  hoverShade: number;
  activeShade: number;
  onClick: () => void;
}

const ActionButton: React.FC<ActionButtonProps> = ({
  iconName,
  label,
  color,
  iconSize,
  iconStroke,
  buttonHeight,
  bgShade,
  iconShade,
  hoverShade,
  activeShade,
  onClick,
}) => {
  const theme = useMantineTheme();

  const bgColor = theme.colors[color]?.[bgShade] || theme.colors.gray[bgShade];
  const icColor = theme.colors[color]?.[iconShade] || theme.colors.gray[iconShade];
  const hoverBgColor = theme.colors[color]?.[hoverShade] || theme.colors.gray[hoverShade];
  const activeBgColor = theme.colors[color]?.[activeShade] || theme.colors.gray[activeShade];

  return (
    <Tooltip label={label} withArrow position="bottom">
      <UnstyledButton
        onClick={onClick}
        aria-label={label}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: buttonHeight,
          flex: 1,
          minWidth: 0,
          borderRadius: theme.radius.md,
          backgroundColor: bgColor,
          transition: "background-color 150ms ease, transform 100ms ease",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = hoverBgColor;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = "scale(1)";
          e.currentTarget.style.backgroundColor = bgColor;
        }}
        onMouseDown={(e) => {
          e.currentTarget.style.transform = "scale(0.96)";
          e.currentTarget.style.backgroundColor = activeBgColor;
        }}
        onMouseUp={(e) => {
          e.currentTarget.style.transform = "scale(1)";
          e.currentTarget.style.backgroundColor = hoverBgColor;
        }}
        onTouchStart={(e) => {
          e.currentTarget.style.transform = "scale(0.96)";
          e.currentTarget.style.backgroundColor = activeBgColor;
        }}
        onTouchEnd={(e) => {
          e.currentTarget.style.transform = "scale(1)";
          e.currentTarget.style.backgroundColor = bgColor;
        }}
      >
        <AppIcon iconName={iconName} size={iconSize} color={icColor} stroke={iconStroke} />
      </UnstyledButton>
    </Tooltip>
  );
};

// ============================================
// Component
// ============================================

/**
 * Displays quick action buttons for vehicle cards (dashboard mode).
 *
 * All sizes use the same UnstyledButton pattern with centered icons.
 * This ensures icon sizes remain consistent regardless of button width.
 *
 * Mobile: Larger icons (28px), brighter color shades
 * Desktop (sm/md/lg): Scaled icons (20-24px), softer color shades
 * Optional scan receipt button rendered as a full-width second row
 */
export const VehicleCardQuickActions: React.FC<VehicleCardQuickActionsProps> = ({
  carId,
  actions = DEFAULT_ACTIONS,
  size = "md",
  onAction,
  showScanReceipt = false,
  onScanComplete,
  onDetectLocation,
  className,
}) => {
  const { t } = useTranslation(["common"]);
  const config = SIZE_CONFIG[size];

  const handleQuickActionClick = (actionType: QuickActionType) => {
    if (onAction) {
      onAction(actionType, carId);
    }
  };

  // Scan Receipt Button
  const scanButton =
    showScanReceipt && onScanComplete ? (
      <ScanReceiptButton
        carId={carId}
        onScanComplete={onScanComplete}
        onDetectLocation={onDetectLocation}
        size={size === "mobile" ? "mobile" : "default"}
      />
    ) : null;

  return (
    <Stack gap="xs" className={className}>
      <Group gap="xs" grow>
        {actions.map((actionType) => {
          const action = QUICK_ACTIONS[actionType];
          if (!action) return null;

          return (
            <ActionButton
              key={actionType}
              iconName={action.iconName}
              label={t(action.labelKey, { defaultValue: actionType })}
              color={action.color}
              iconSize={config.iconSize}
              iconStroke={config.iconStroke}
              buttonHeight={config.buttonHeight}
              bgShade={config.bgShade}
              iconShade={config.iconShade}
              hoverShade={config.hoverShade}
              activeShade={config.activeShade}
              onClick={() => handleQuickActionClick(actionType)}
            />
          );
        })}
      </Group>
      {scanButton}
    </Stack>
  );
};